import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {getLocalisedPropertyValue} from '../../../../../utils/getLocalisedPropertyValue';
import type {DepartmentHealthActionItem} from '../../types/DepartmentHealthTypes';
import {getDepartmentHealthActionItemIcon} from '../../utils/DepartmentHealth/getDepartmentHealthActionItemIcon';
import {
  departmentHealthActionCardBg,
  getDepartmentHealthActionItemStyles,
  getDepartmentHealthActionPriorityStyles,
} from '../../utils/DepartmentHealth/getDepartmentHealthActionItemStyles';

export interface Props {
  item: DepartmentHealthActionItem;
}

const priorityBadgeSx = {
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  px: 1,
  py: 0.25,
  borderRadius: '5px',
  fontSize: '10px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  border: '0.5px solid',
  flexShrink: 0,
  whiteSpace: 'nowrap',
} as const;

export const DepartmentHealthRecommendedActionItem = ({item}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.recommendedActions'});
  const currentLanguage = useContext(LanguageContext);
  const recommendation = getLocalisedPropertyValue(item.recommendation, currentLanguage);
  const expectedOutcome = getLocalisedPropertyValue(item.expectedOutcome, currentLanguage);
  const typeStyles = getDepartmentHealthActionItemStyles(item.actionType);
  const priorityStyles = item.priority ? getDepartmentHealthActionPriorityStyles(item.priority) : null;

  return (
    <Box
      data-testid='department-health-action-item'
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 1.875,
        py: 1.625,
        borderRadius: '10px',
        border: '0.5px solid',
        borderColor: 'divider',
        bgcolor: departmentHealthActionCardBg,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: '1px',
          bgcolor: typeStyles.iconBg,
          color: typeStyles.iconColor,
          border: '0.5px solid',
          borderColor: typeStyles.iconBorder,
        }}
      >
        {getDepartmentHealthActionItemIcon(item.actionType)}
      </Box>

      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography
          sx={{
            fontSize: '10.5px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: typeStyles.iconColor,
            mb: 0.25,
          }}
        >
          {t(`types.${item.actionType}`)}
        </Typography>

        <Typography
          sx={{
            fontSize: '12.5px',
            fontWeight: 500,
            lineHeight: 1.55,
            color: 'text.primary',
            mb: 0.375,
          }}
        >
          {recommendation}
        </Typography>

        <Typography sx={{fontSize: '11.5px', lineHeight: 1.55, color: 'text.secondary'}}>
          {expectedOutcome}
        </Typography>
      </Box>

      {priorityStyles && item.priority && (
        <Box
          component='span'
          sx={{
            ...priorityBadgeSx,
            bgcolor: priorityStyles.bgcolor,
            color: priorityStyles.color,
            borderColor: priorityStyles.borderColor,
          }}
        >
          {t(`priority.${item.priority}`)}
        </Box>
      )}
    </Box>
  );
};
